import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { MediumText } from '../../lib/Text';
import BusinessInfoFooter from '../../components/BusinessInfoFooter';
import CoachReportModal from './CoachReportModal';

/**
 * 운영 리포트 레이아웃 — 상단 탭.
 * 탭 스타일은 CenterReportLayout 과 동일하게 맞춰 학습 비용을 없앤다.
 */

type OpsTab = {
    label: string;
    path: string;
    isNew?: boolean;
};

const TABS: OpsTab[] = [
    { label: '오늘의 시그널', path: '/opsBriefing/today', isNew: true },
    { label: '회원 현황', path: '/opsBriefing/members' },
    { label: '강사 현황', path: '/opsBriefing/coaches' },
    { label: '지난 리포트', path: '/opsBriefing/history' },
];

const OpsBriefingLayout = () => {
    const navigate = useNavigate();
    const location = useLocation();
    /*
     * 강사 리포트는 특정 탭의 기능이 아니다.
     * 어느 화면을 보다가도 떠오르는 일이라 상단에 상시로 둔다.
     */
    const [showReport, setShowReport] = useState(false);
    const isActiveScreen = (path: string) => location.pathname.includes(path);

    // 첫 진입 시 오늘의 브리핑으로
    useEffect(() => {
        if (location.pathname === '/opsBriefing' || location.pathname === '/opsBriefing/') {
            navigate(TABS[0].path, { replace: true });
        }
    }, [location.pathname, navigate]);

    return (
        /*
         * 🔴 `wordBreak: 'keep-all'` — 한글이 **낱자 사이에서 꺾이지 않게** 한다.
         *
         * 브라우저 기본값(`normal`)은 한중일 문자를 글자 단위로 끊을 수 있어서,
         * 폭이 좁아지면 「이은수 회 / 원」·「2,252,500 / 원」처럼 단어와 단위가
         * 중간에서 갈라진다. 영문은 원래 단어 단위로 끊기니 이 문제가 없다.
         *
         * 이 탭 묶음(오늘의 브리핑·회원 현황·강사 현황·지난 리포트)이 전부 이
         * 컨테이너 안에 있어서 한 곳에서 잡는다 — 화면마다 따로 붙이면 새 화면이
         * 생길 때마다 빠뜨린다.
         * ⚠️ 아침 팝업은 이 트리 밖(PrivateRoutes)이라 거기서 따로 잡는다.
         */
        <div
            className="flex flex-col items-center w-full h-full bg-[#F4F5F7]"
            style={{ wordBreak: 'keep-all' }}
        >
            <div className="w-full max-w-[1440px]">
                <div
                    className="flex w-full items-center justify-between gap-[16px]"
                    style={{ marginTop: 108 }}
                >
                    <div className="flex flex-wrap items-center gap-[6px] rounded-[16px] border border-solid border-[#E2E8F0] bg-white p-[6px] shadow-[0_8px_24px_rgba(26,48,93,0.06)]">
                        {TABS.map((tab) => {
                            const isActive = isActiveScreen(tab.path);
                            return (
                                <button
                                    type="button"
                                    aria-label={`${tab.label} 탭`}
                                    className="h-[44px] rounded-[11px] px-[22px] transition-colors"
                                    key={tab.path}
                                    onClick={() => navigate(tab.path)}
                                    style={{
                                        border: 'none',
                                        background: isActive ? '#1A305D' : 'transparent',
                                        cursor: 'pointer',
                                        boxShadow: isActive ? '0 6px 16px rgba(26,48,93,0.18)' : 'none',
                                        position: 'relative',
                                    }}
                                >
                                    <MediumText
                                        fontSize={18}
                                        lineHeight={24}
                                        color={isActive ? '#FFFFFF' : '#8B95A5'}
                                        letterSpacing={0}
                                        className="cursor-pointer whitespace-nowrap"
                                    >
                                        {tab.label}
                                    </MediumText>
                                    {tab.isNew && (
                                        <span
                                            style={{
                                                position: 'absolute',
                                                top: -7,
                                                right: 8,
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                borderRadius: 999,
                                                background: '#EF4444',
                                                color: '#FFFFFF',
                                                fontSize: 9,
                                                lineHeight: '12px',
                                                fontWeight: 800,
                                                padding: '1px 5px',
                                                boxShadow: '0 2px 6px rgba(239,68,68,0.35)',
                                            }}
                                        >
                                            NEW
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    <button
                        type="button"
                        onClick={() => setShowReport(true)}
                        aria-label="강사 성과 리포트 열기"
                        className="flex flex-none items-center rounded-[12px]"
                        style={{
                            height: 52,
                            padding: '0 22px',
                            gap: 9,
                            background: '#1A305D',
                            border: 'none',
                            color: '#FFFFFF',
                            fontSize: 16,
                            fontWeight: 700,
                            cursor: 'pointer',
                            boxShadow: '0 8px 20px rgba(26,48,93,0.22)',
                        }}
                    >
                        <span style={{ fontSize: 17 }}>📄</span>
                        강사 성과 리포트
                    </button>
                </div>

                <div style={{ marginTop: 28 }}>
                    <Outlet />
                </div>
            </div>

            <div style={{ width: '100%', marginTop: 40 }}>
                <BusinessInfoFooter />
            </div>

            {showReport && <CoachReportModal onClose={() => setShowReport(false)} />}
        </div>
    );
};

export default OpsBriefingLayout;
